(() => {
  if (window.AwattSite) return;
  const ownerDocument = document;
  const ownerWindow = window;
  const asset = name => {
    if (!name) return '';
    const key = String(name).replace(/^\/assets\//, '');
    if (/^(https?:|data:|blob:)/.test(key)) return key;
    const direct = ownerWindow.AwattSiteAssets?.[key];
    if (direct) return direct;
    const base = ownerWindow.AwattSiteAssets?.['site.css'];
    return base ? new URL(`awatt-site-${key.replaceAll('/', '-')}`, base).href : '';
  };
  const route = value => {
    const input = String(value || '');
    if (!input.startsWith('/') || input.startsWith('//')) return input;
    const split = input.search(/[?#]/);
    const pathname = (split < 0 ? input : input.slice(0, split)).replace(/\/$/, '') || '/';
    const suffix = split < 0 ? '' : input.slice(split);
    const target = ownerWindow.AwattSiteRouteMap?.[pathname] || pathname;
    if (!target.includes('?') && !target.includes('#')) return target + suffix;
    const destination = new URL(target, ownerWindow.location.origin);
    const incoming = new URL(input, ownerWindow.location.origin);
    if (suffix.startsWith('?')) incoming.searchParams.forEach((value, key) => destination.searchParams.set(key, value));
    if (suffix.includes('#')) destination.hash = incoming.hash;
    return /^https?:/.test(target) ? destination.href : destination.pathname + destination.search + destination.hash;
  };
  const roots = scope => {
    const candidates = [...(scope.querySelectorAll?.('.awatt-site') || [])];
    if (scope.matches?.('.awatt-site')) candidates.unshift(scope);
    return candidates.filter(root => !candidates.some(other => other !== root && other.contains(root)));
  };
  const resolveGallery = gallery => (gallery || []).map(view => ({
    ...view,
    image: asset(view.image),
    thumb: asset(view.thumb),
    ...(view.video ? { video: asset(view.video) } : {}),
    ...(view.srcset ? { srcset: view.srcset.split(',').map(part => {
      const [name, ...size] = part.trim().split(/\s+/);
      return [asset(name), ...size].join(' ');
    }).join(', ') } : {})
  }));
  function context(root) {
    const listeners = [];
    const timeouts = new Set();
    const intervals = new Set();
    const frames = new Set();
    const observers = new Set();
    let destroyed = false;
    const listen = (target, type, callback, options) => {
      target.addEventListener(type, callback, options);
      listeners.push([target, type, callback, options]);
    };
    const setTimeout = (callback, delay, ...args) => {
      if (destroyed) return 0;
      const id = ownerWindow.setTimeout(() => { timeouts.delete(id); if (!destroyed) callback(...args); }, delay);
      timeouts.add(id);
      return id;
    };
    const clearTimeout = id => { timeouts.delete(id); ownerWindow.clearTimeout(id); };
    const setInterval = (callback, delay, ...args) => {
      if (destroyed) return 0;
      const id = ownerWindow.setInterval(() => { if (!destroyed) callback(...args); }, delay);
      intervals.add(id);
      return id;
    };
    const clearInterval = id => { intervals.delete(id); ownerWindow.clearInterval(id); };
    const requestAnimationFrame = callback => {
      if (destroyed) return 0;
      const id = ownerWindow.requestAnimationFrame(time => { frames.delete(id); if (!destroyed) callback(time); });
      frames.add(id);
      return id;
    };
    const cancelAnimationFrame = id => { frames.delete(id); ownerWindow.cancelAnimationFrame(id); };
    const wrapObserver = Native => Native ? class extends Native {
      constructor(callback, options) {
        super((entries, observer) => { if (!destroyed) callback(entries, observer); }, options);
        observers.add(this);
      }
    } : undefined;
    const matchMedia = query => {
      const media = ownerWindow.matchMedia(query);
      return new Proxy(media, { get(target, key) {
        if (key === 'addEventListener') return (type, callback, options) => listen(target, type, callback, options);
        const value = Reflect.get(target, key, target);
        return typeof value === 'function' ? value.bind(target) : value;
      } });
    };
    const document = new Proxy(ownerDocument, { get(target, key) {
      if (key === 'querySelector') return selector => root.querySelector(selector);
      if (key === 'querySelectorAll') return selector => root.querySelectorAll(selector);
      if (key === 'addEventListener') return (type, callback, options) => listen(target, type, callback, options);
      const value = Reflect.get(target, key, target);
      return typeof value === 'function' ? value.bind(target) : value;
    } });
    const overrides = { setTimeout, clearTimeout, setInterval, clearInterval, requestAnimationFrame, cancelAnimationFrame, matchMedia };
    const window = new Proxy(ownerWindow, { get(target, key) {
      if (key in overrides) return overrides[key];
      if (key === 'addEventListener') return (type, callback, options) => listen(target, type, callback, options);
      const value = Reflect.get(target, key, target);
      return typeof value === 'function' ? value.bind(target) : value;
    } });
    return {
      document, window, ...overrides,
      IntersectionObserver: wrapObserver(ownerWindow.IntersectionObserver),
      ResizeObserver: wrapObserver(ownerWindow.ResizeObserver),
      destroy() {
        destroyed = true;
        listeners.forEach(([target, type, callback, options]) => target.removeEventListener(type, callback, options));
        timeouts.forEach(id => ownerWindow.clearTimeout(id));
        intervals.forEach(id => ownerWindow.clearInterval(id));
        frames.forEach(id => ownerWindow.cancelAnimationFrame(id));
        observers.forEach(observer => observer.disconnect());
      }
    };
  }
  ownerWindow.awattSiteAsset = asset;
  ownerWindow.awattSiteRoute = route;
  ownerWindow.AwattSite = { asset, route, roots, resolveGallery, context, modules: new Set() };
})();
